/**
 * Service health predicates — pure rules shared by the duplicate guard on the
 * manual-add path, the deal-driven dedup pool, and the /home/services badges.
 *
 * Deliberately Firebase-free so the creation services, the page and the
 * firebase-admin audit CLI apply the SAME rules (a badge that disagrees with
 * the guard is worse than neither) and so the tests can call them directly.
 */

import { normalizeAddress } from '@/utils/address';

/** Statuses that take a service out of play — a dead service is never a duplicate. */
export function isLiveServiceStatus(status?: string): boolean {
  return status !== 'Cancelled' && status !== 'Inactive';
}

/**
 * Do two service addresses refer to the same site?
 *
 * Missing on either side counts as agreement: the deal/install creation paths
 * write no address at all, so requiring one would make every auto-created
 * service invisible to dedup — which is exactly how the Aug 2026 Starlink pair
 * survived. Two DIFFERENT addresses never match, so multi-property customers
 * (two NBN services, two Starlink kits) stay safe.
 */
export function serviceAddressesAgree(a?: string, b?: string): boolean {
  const x = normalizeAddress(a || '');
  const y = normalizeAddress(b || '');
  if (!x || !y) return true;
  return x === y;
}

/** Minimal shape needed to cluster duplicates — structurally satisfied by CustomerService. */
export interface DuplicateCandidate {
  id?: string;
  customerId?: string;
  serviceType?: string;
  address?: string;
  status?: string;
  bundleId?: string;
}

/**
 * Ids of every service that shares (customer, service type, site) with another
 * live service — i.e. the same physical service recorded twice.
 *
 * Bundle members are excluded: a bundle deliberately holds several services for
 * one customer at one site, and the Bundle badge already explains them.
 *
 * Pairwise rather than keyed, because an address-less record matches ANY
 * address for the same customer — that can't be expressed as a group key.
 */
export function findDuplicateServiceIds(services: DuplicateCandidate[]): Set<string> {
  const byCustomerType = new Map<string, DuplicateCandidate[]>();
  for (const s of services) {
    if (!s.id || s.bundleId || !isLiveServiceStatus(s.status)) continue;
    const key = `${s.customerId ?? ''}|${(s.serviceType ?? '').toLowerCase()}`;
    const list = byCustomerType.get(key);
    if (list) list.push(s);
    else byCustomerType.set(key, [s]);
  }

  const duplicates = new Set<string>();
  for (const group of byCustomerType.values()) {
    if (group.length < 2) continue;
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        if (serviceAddressesAgree(group[i].address, group[j].address)) {
          duplicates.add(group[i].id!);
          duplicates.add(group[j].id!);
        }
      }
    }
  }
  return duplicates;
}

/** Minimal shape needed for the price check — satisfied by CustomerService. */
export interface PriceCandidate {
  billingType?: string;
  status?: string;
  monthlyPrice?: number;
  bundleId?: string;
  priceHistory?: unknown[];
}

/**
 * A live recurring service carrying no positive monthly price: it contributes
 * $0 to MRR and nothing will ever bill it.
 *
 * Almost always means the plan lookup failed when the service was created —
 * `activateServicePlansForCustomer` / `createServicesAtBilling` set
 * `monthlyPrice = plan?.sellPrice`, so a deal whose intake plan name no longer
 * matches an active `service_plans` doc yields a priceless service and only a
 * console warning. Surfacing it on the card is the difference between "we
 * noticed" and "it silently didn't bill".
 *
 * A deliberate $0 (goodwill, comped, retention) leaves a priceHistory entry, so
 * a recorded price decision suppresses the warning.
 */
/** Minimal shape for the Starlink supplier-price check. */
export interface StarlinkPriceCandidate {
  serviceType?: string;
  monthlyPrice?: number;
  metadata?: { starlinkCurrentBalance?: unknown;[k: string]: unknown };
}

/**
 * Is this Starlink service's price the SUPPLIER's balance rather than what the
 * customer agreed to pay?
 *
 * Until Aug 2026 the Starlink email ingest wrote the account's current balance
 * straight to `monthlyPrice`. That is a balance not a plan price, it is what
 * Starlink charges us not what the customer pays, it is GST-inclusive in an
 * ex-GST field, and it was written to every kit on a shared account. The fix
 * stopped new overwrites; it could not undo the ones already made.
 *
 * Detection is an exact match against the last balance the ingest recorded, so
 * it flags the records to re-check against the signed proposal. A customer
 * whose agreed price genuinely equals the Starlink balance is a false positive
 * — reviewing it costs a glance.
 */
export function hasSupplierDerivedStarlinkPrice(service: StarlinkPriceCandidate): boolean {
  if (service.serviceType !== 'Starlink') return false;
  const balance = service.metadata?.starlinkCurrentBalance;
  if (typeof balance !== 'number' || typeof service.monthlyPrice !== 'number') return false;
  return Math.round(balance * 100) === Math.round(service.monthlyPrice * 100);
}

export function isMissingRecurringPrice(service: PriceCandidate): boolean {
  if (service.billingType === 'one-time') return false;
  if (service.bundleId) return false; // members legitimately hold 0
  if (service.status !== 'Active' && service.status !== 'Suspended') return false;
  if (typeof service.monthlyPrice === 'number' && service.monthlyPrice > 0) return false;
  if (service.priceHistory && service.priceHistory.length > 0) return false;
  return true;
}
